/* eslint-disable @typescript-eslint/camelcase */
/* eslint-disable camelcase */
import { actions as ACTIONS, DEFAULT_PRESET, DEFAULT_PRESET_OPTION, mutations as TYPES } from './types';
import type { Commit } from 'vuex';
import cloneDeep from 'lodash.clonedeep';
import { app as globalStore } from '@/store';
import storeLocal from '@/app/store/store';
import type {
  PresetOptionParams,
  PresetOptionRequest,
  PresetOptionResponse,
  PresetOptionsRequest,
  PresetParams,
  PresetResponse,
  Preset,
  PresetOption,
  PresetOptionBorder,
  PresetOptionButton,
  PresetOptionColor,
  PresetOptionCorner,
  PresetOptionSpacing,
  PresetOptionText,
} from '@modules/global-style';
import { PresetGenerator, generateCssString, globalStyleService } from '@modules/global-style';
import type { State } from './state';
import { editorServices } from '../services';

const parsePresetOption = (presetOptionResponse: PresetOptionResponse): PresetOption => {
  if (!presetOptionResponse) {
    return DEFAULT_PRESET_OPTION;
  }

  const presetOptionResult: PresetOption = {
    ...presetOptionResponse,
    isDefault: presetOptionResponse.is_default,
    type: presetOptionResponse.preset_option_type,
    destroy: !!presetOptionResponse._destroy,
    subType: presetOptionResponse.sub_type,
  };

  delete Object(presetOptionResult).is_default;
  delete Object(presetOptionResult).preset_option_type;
  delete Object(presetOptionResult).sub_type;
  delete Object(presetOptionResult)._destroy;

  return presetOptionResult;
};

const parsePreset = (presetResponse: PresetResponse): Preset => {
  if (!presetResponse) {
    return DEFAULT_PRESET;
  }

  const presetOptionResponse = presetResponse.preset_options?.map(parsePresetOption);
  const presetOptionResponseColor = presetOptionResponse?.filter((p) => p.type === 'color') as PresetOptionColor[];
  if (!presetOptionResponseColor[0].value.accent?.hasOwnProperty('color7')) {
    presetOptionResponseColor[0].value.accent.color7 = '#00000000';
  }

  const presetResult: Preset = {
    ...presetResponse,
    destroy: !!presetResponse._destroy,
    presetOptions: {
      texts: presetOptionResponse?.filter((p) => p.type === 'text') as PresetOptionText[],
      colors: presetOptionResponseColor,
      borders: presetOptionResponse?.filter((p) => p.type === 'border') as PresetOptionBorder[],
      corners: presetOptionResponse?.filter((p) => p.type === 'corner') as PresetOptionCorner[],
      spacings: presetOptionResponse?.filter((p) => p.type === 'spacing') as PresetOptionSpacing[],
      buttons: presetOptionResponse?.filter((p) => p.type === 'button') as PresetOptionButton[],
    },
  };

  delete Object(presetResult).preset_options;
  delete Object(presetResult)._destroy;

  return presetResult;
};

const parseDataCreateNewPreset = (nPreset: any) => {
  const packs = [];
  const parsePacks: any = [];
  const oPreset = nPreset.presetOptions;

  packs.push(
    ...oPreset.borders,
    ...oPreset.texts,
    ...oPreset.colors,
    ...oPreset.spacings,
    ...oPreset.buttons,
    ...oPreset.corners,
  );

  packs.map((pack) => {
    parsePacks.push({
      name: pack.name,
      key: pack.key,
      // eslint-disable-next-line camelcase,@typescript-eslint/camelcase
      is_default: pack.isDefault,
      // eslint-disable-next-line camelcase,@typescript-eslint/camelcase
      preset_option_type: pack.type,
      value: pack.value,
    });
  });

  return {
    name: nPreset.name,
    // eslint-disable-next-line camelcase,@typescript-eslint/camelcase
    preset_options: [...parsePacks],
  };
};

const parsePresetSaveOptions = (data: PresetOptionParams): PresetOptionsRequest => {
  const presetsRequest: PresetOptionRequest[] = [];
  const presetRequest: PresetOptionsRequest = {
    preset_options: [],
    css_str: data.cssStr,
  };

  data.presetOptions.map((preset: PresetOption) => {
    const presetD: PresetOptionRequest = {
      id: preset.id,
      name: preset.name,
      key: preset.key,
      // eslint-disable-next-line camelcase,@typescript-eslint/camelcase
      preset_option_type: preset.type,
      _destroy: preset.destroy,
      value: preset.value,
    };

    presetsRequest.push(presetD);
  });

  // eslint-disable-next-line camelcase,@typescript-eslint/camelcase
  presetRequest.preset_options = presetsRequest;
  return presetRequest;
};

const actions = {
  [ACTIONS.PUBLIC_PRESET]({ state }: { state: State }, params: { preset: any }) {
    return new Promise(async (resolve, reject) => {
      try {
        const preset = params?.preset;
        if (!preset) return;
        let content = '';
        if (preset) {
          content = generateCssString(
            Object.prototype.hasOwnProperty.call(preset, 'presetOptions') ? preset : parsePreset(preset),
          );
        }
        const response = await globalStyleService.publishPreset(preset.id, content);
        resolve(response);
      } catch (e) {
        reject(e);
      }
    });
  },
  [ACTIONS.FETCH_SNIPPETS]({ state, commit }: { state: State; commit: Commit }) {
    return new Promise(async (resolve, reject) => {
      try {
        commit(TYPES.SET_LOADING_SNIPPETS, true);
        const response = await editorServices.apiGetElements();
        if (response.data) {
          commit(
            TYPES.SET_SNIPPERS,
            //@ts-ignore
            response.data.concat({
              id: -1,
              name: 'Other Elements',
              key: 'other_elements',
              snippets: [],
            }),
          );

          const dataSettings: any = {};

          (response.data || []).forEach((category) => {
            category.snippets.forEach((item) => {
              dataSettings[item.key] = item.html;
            });
          });
          globalStore.$store.commit('editor/SET_ALL_DATA_SNIPPETS', dataSettings);
          storeLocal.setValue('__snippets', true);
          resolve(response.data);
        }
      } catch (e) {
        reject(e);
      } finally {
        commit(TYPES.SET_LOADING_SNIPPETS, false);
      }
    });
  },
  [ACTIONS.APPLY_PRESET](
    { state, commit }: { state: State; commit: Commit },
    params: { pageId: number; presetId: number },
  ) {
    return new Promise(async (resolve, reject) => {
      try {
        const applyRes = await globalStyleService.applyPresetToPage(params);
        if (applyRes.status == 'success') {
          commit(TYPES.SET_LINKED_PRESET_ID, params.presetId);
        }
        resolve(applyRes);
      } catch (e) {
        reject(e);
      }
    });
  },
  [ACTIONS.CRAWL_FROM_LIVE_THEME]({ commit, dispatch }: { commit: Commit; dispatch: any }, domThisPage: string) {
    return new Promise(async (resolve, reject) => {
      try {
        let rawData: any;
        let source: string;
        if (domThisPage) {
          rawData = domThisPage;
          source = 'Page Preset';
        } else {
          rawData = await globalStyleService.crawlHtmlSourceLiveTheme();

          if (rawData.status === 'error') resolve(rawData);

          source = 'Live Preset';
          rawData = rawData.data;
        }

        PresetGenerator({ dom: rawData })
          .then(async (preset: any) => {
            const res = await dispatch(ACTIONS.CREATE_PRESET, { preset: preset, source: source });

            resolve(res);
          })
          .catch((err: any) => {
            reject(err);
          });
      } catch (e) {
        reject(e);
      }
    });
  },
  [ACTIONS.CREATE_PRESET](
    { state, commit }: { state: State; commit: Commit },
    payload: { preset: any; source: string },
  ) {
    return new Promise(async (resolve, reject) => {
      try {
        const params = parseDataCreateNewPreset(cloneDeep(payload.preset));
        params.name = `${payload.source} ${state.presets.length}`;
        const mPreset = await globalStyleService.createNewPreset(params);

        // NOTE (thanhdx)L for test create preset local
        // const mPreset = {
        //   status: "success",
        //   data: {
        //     name: params.name,
        //     // eslint-disable-next-line camelcase, @typescript-eslint/camelcase
        //     preset_options: params.preset_options,
        //     _destroy: false,
        //     id: 999
        //   }
        // };
        if (mPreset.status == 'success') {
          const mPresetData = mPreset.data;
          mPreset.data = parsePreset(mPresetData) as any;

          if (mPreset && mPreset.data) {
            state.presets = state.presets.concat(mPreset.data as any);
          }
        }
        resolve(mPreset);
      } catch (e) {
        reject(e);
      }
    });
  },
  [ACTIONS.FETCH_PRESETS]({ commit }: { commit: Commit }) {
    return new Promise(async (resolve, reject) => {
      try {
        const presetsResponse = await globalStyleService.getPresets();
        const presets = presetsResponse?.data?.map(parsePreset);
        commit(TYPES.COMMIT_PRESETS, presets);
        commit(TYPES.SET_LOADING_PRESETS, false);
        resolve(presets);
      } catch (e) {
        reject(e);
      }
    });
  },
  [ACTIONS.FETCH_PAGES]({ commit }: { commit: Commit }, params: any) {
    return new Promise(async (resolve, reject) => {
      try {
        const response = await globalStyleService.getPages(params);
        commit(TYPES.COMMIT_PAGES, response.data);
        resolve(response);
      } catch (e) {
        reject(e);
      }
    });
  },
  [ACTIONS.SAVE_PRESET_OPTIONS]({ _ }: any, params: PresetOptionParams) {
    return new Promise(async (resolve, reject) => {
      try {
        const data = parsePresetSaveOptions(params);
        const savePreset = await globalStyleService.savePresetOptions(
          {
            id: params.presetId,
            type: params.type,
          },
          data,
        );
        if (savePreset.errorCode === 'GS00006') {
          resolve(savePreset);
        } else {
          resolve(parsePreset(savePreset.data));
        }
      } catch (e) {
        reject(e);
      }
    });
  },
  [ACTIONS.UPDATE_PRESET]({ state }: { state: State }, params: any) {
    return new Promise(async (resolve, reject) => {
      try {
        const updatePreset = await globalStyleService.updatePreset(params.presetId, params.data);
        if (updatePreset.status == 'success') {
          const indexUpdate = state.presets.findIndex((item) => item.id === params.presetId);
          if (indexUpdate !== -1) {
            state.presets[indexUpdate] = { ...state.presets[indexUpdate], ...params.data };
            state.presets = [...state.presets];
          }
        }
        resolve(updatePreset);
      } catch (e) {
        reject(e);
      }
    });
  },
  [ACTIONS.DUPLICATE_PRESET]({ state }: { state: State }, params: any) {
    return new Promise(async (resolve, reject) => {
      try {
        const duplicatePreset = await globalStyleService.duplicatePreset(params.presetId);
        if (duplicatePreset.status == 'success') {
          const parseData = parsePreset(duplicatePreset?.data);
          state.presets.push(parseData);
        }
        resolve(duplicatePreset);
      } catch (e) {
        reject(e);
      }
    });
  },
  [ACTIONS.DELETE_PRESET]({ state }: { state: State }, params: PresetParams) {
    return new Promise(async (resolve, reject) => {
      try {
        const deletePreset = await globalStyleService.deletePreset(params.presetId);
        if (deletePreset.status == 'success') {
          const indexDelete = state.presets.findIndex((item) => item.id === params.presetId);
          if (indexDelete !== -1) {
            state.presets.splice(indexDelete, 1);
          }
        }
        resolve(deletePreset);
      } catch (e) {
        reject(e);
      }
    });
  },
  [ACTIONS.UPDATE_LINKED_PRESET_LIST]({ state, rootState, commit }: any, preset: Preset) {
    const pageEditing = rootState.app.pageInfo?.page;
    const gId = pageEditing?.g_id;
    let presets = cloneDeep(state.presets);
    presets = presets.map((item: any) => {
      if (!item.linkedPages) {
        item.linkedPages = [];
      }
      const index = item.linkedPages.findIndex((j: any) => j.g_id == gId);
      if (item.id == preset.id) {
        if (index == -1) {
          item.linkedPages.push(pageEditing);
          commit(TYPES.SET_CURRENT_PRESET, cloneDeep(item));
        }
      } else {
        if (index != -1) {
          item.linkedPages.splice(index, 1);
        }
      }
      return item;
    });
    state.presets = [...presets];
  },
};

export default actions;
